import CampTypes from '../data/CampTypes';
const { LANDING_PAGES } = require('../seo/landingPages');

const SITE_URL = 'https://www.snapcamp.ai';
const LANDING_ROUTE_PATTERN = /^\/(summer-camps|kids-camps)\/([^/?#]+)\/?$/i;

type ExploreUrlState = {
    address?: string;
    startDate?: Date | null;
    endDate?: Date | null;
    age?: number | null;
    gender?: string | null;
    view?: 'camps' | 'sessions';
    categories?: string[];
};

const toTitleCase = (value: string) =>
    value
        .split(' ')
        .filter(Boolean)
        .map((word) => {
            if (word.length === 2) {
                return word.toUpperCase();
            }

            return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');

export const normalizeExploreAddress = (value?: string | null) =>
    String(value || '')
        .replace(/\s*,\s*(usa|united states)$/i, '')
        .replace(/\s+/g, ' ')
        .trim();

export const slugifyExploreSegment = (value?: string | null) =>
    normalizeExploreAddress(value)
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');

export const deslugifyExploreSegment = (value?: string | null) =>
    toTitleCase(
        decodeURIComponent(String(value || ''))
            .replace(/-/g, ' ')
            .replace(/\s+/g, ' ')
            .trim()
    );

export const isExploreLandingPath = (pathname: string) => LANDING_ROUTE_PATTERN.test(pathname);

export const isExploreLikePath = (pathname: string) =>
    pathname === '/'
    || pathname === '/explore'
    || isExploreLandingPath(pathname);

const normalizeCategorySlugs = (categories?: string[]) =>
    (categories || [])
        .map((category) => {
            const matchingType = CampTypes.find((type) => slugifyExploreSegment(type.text) === slugifyExploreSegment(category));
            return matchingType?.text;
        })
        .filter((value): value is string => !!value && value !== 'All');

export const parseExploreUrl = (pathname: string, search: string) => {
    const params = new URLSearchParams(search || '');
    const landingMatch = pathname.match(LANDING_ROUTE_PATTERN);
    const routeType = landingMatch?.[1]?.toLowerCase() || null;
    const locationSlug = landingMatch?.[2] || '';
    const parsedAge = params.get('age');

    const address = locationSlug
        ? deslugifyExploreSegment(locationSlug)
        : undefined;

    return {
        routeType,
        address,
        startDate: params.get('start') ? new Date(`${params.get('start')}T00:00:00`) : null,
        endDate: params.get('end') ? new Date(`${params.get('end')}T00:00:00`) : null,
        age: parsedAge && !Number.isNaN(Number(parsedAge)) ? Number(parsedAge) : null,
        gender: params.get('gender') || null,
        view: params.get('view') === 'sessions' ? 'sessions' : 'camps',
        categories: normalizeCategorySlugs(
            (params.get('categories') || '')
                .split(',')
                .map((value) => value.trim())
                .filter(Boolean)
        ),
        hasShareableState:
            !!locationSlug
            || params.has('start')
            || params.has('end')
            || params.has('age')
            || params.has('gender')
            || params.has('view')
            || params.has('categories'),
    };
};

const formatDateParam = (value?: Date | null) => {
    if (!value) return null;
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toISOString().slice(0, 10);
};

export const buildExploreUrl = ({
    address,
    startDate,
    endDate,
    age,
    gender,
    view = 'camps',
    categories = [],
}: ExploreUrlState) => {
    const normalizedAddress = normalizeExploreAddress(address);
    const isNearby = !normalizedAddress || normalizedAddress.toLowerCase() === 'nearby';
    const pathname = isNearby
        ? '/explore'
        : `/summer-camps/${slugifyExploreSegment(normalizedAddress)}`;

    const params = new URLSearchParams();
    const formattedStart = formatDateParam(startDate);
    const formattedEnd = formatDateParam(endDate);

    if (formattedStart) params.set('start', formattedStart);
    if (formattedEnd) params.set('end', formattedEnd);
    if (typeof age === 'number') params.set('age', String(age));
    if (gender) params.set('gender', gender);
    if (view === 'sessions') params.set('view', 'sessions');

    const normalizedCategories = normalizeCategorySlugs(categories);
    if (normalizedCategories.length > 0) {
        params.set(
            'categories',
            normalizedCategories.map((category) => slugifyExploreSegment(category)).join(',')
        );
    }

    const query = params.toString();
    return query ? `${pathname}?${query}` : pathname;
};

export const getExploreRouteMeta = (pathname: string) => {
    const configuredLandingPage = LANDING_PAGES.find((page: any) => page.pathname === pathname);
    if (configuredLandingPage) {
        return {
            title: configuredLandingPage.title,
            description: configuredLandingPage.description,
            canonical: `${SITE_URL}${configuredLandingPage.pathname}`,
            keywords: configuredLandingPage.keywords,
        };
    }

    const landingMatch = pathname.match(LANDING_ROUTE_PATTERN);

    if (!landingMatch) {
        return null;
    }

    const routeType = landingMatch[1].toLowerCase();
    const locationName = deslugifyExploreSegment(landingMatch[2]);
    const titlePrefix = routeType === 'kids-camps' ? 'Kids Camps' : 'Summer Camps';

    return {
        title: `${titlePrefix} in ${locationName} | SnapCamp`,
        description: `Browse ${titlePrefix.toLowerCase()} in ${locationName} on SnapCamp. Explore trusted programs, compare listings, and find the right camp for your family.`,
        canonical: `${SITE_URL}${pathname}`,
        keywords: `summer camps in ${locationName.toLowerCase()}, kids camps in ${locationName.toLowerCase()}, ${locationName.toLowerCase()} summer programs, ${locationName.toLowerCase()} camp listings`,
    };
};

export const getExploreLandingPage = (pathname: string) => {
    const configuredLandingPage = LANDING_PAGES.find((page: any) => page.pathname === pathname);
    if (configuredLandingPage) {
        return configuredLandingPage;
    }

    const landingMatch = pathname.match(LANDING_ROUTE_PATTERN);
    if (!landingMatch) {
        return null;
    }

    const routeType = landingMatch[1].toLowerCase();
    const locationName = deslugifyExploreSegment(landingMatch[2]);
    const titlePrefix = routeType === 'kids-camps' ? 'Kids Camps' : 'Summer Camps';

    return {
        pathname,
        heading: `${titlePrefix} in ${locationName}`,
        title: `${titlePrefix} in ${locationName} | SnapCamp`,
        description: `Browse ${titlePrefix.toLowerCase()} in ${locationName} on SnapCamp. Explore trusted programs, compare listings, and find the right camp for your family.`,
        intro: `Use this ${locationName} landing page to explore camp listings, compare nearby options, and refine your search by dates, age, and camp type.`,
        body: [
            `SnapCamp brings ${locationName} camp discovery into one place so families can move from search to comparison faster.`,
        ],
    };
};
